import {
  type BrowserSessionSnapshot,
  type BrowserTabSnapshot,
  type DevTunnelRecord,
  type EnvironmentId,
  type ThreadId,
} from "@cmmd-forge/contracts";
import { type Dispatch, type MutableRefObject, type SetStateAction, useCallback } from "react";
import { ensureEnvironmentApi } from "~/environmentApi";
import { toastManager } from "./ui/toast";
import {
  type BrowserAddressCommand,
  browserRememberedAddress,
  buildBrowserAddressCommand,
  buildInvalidBrowserAddressMessage,
  hasReusableDevTunnelAccess,
  localDevServerPreviewPort,
  resolveBrowserAddressSubmitAction,
  upsertDevTunnelRecord,
} from "./BrowserPreviewPanel.logic";

interface EnsureBrowserSessionOptions {
  readonly allowWhileConnecting?: boolean;
  readonly retryHiddenLocalPreviewFailure?: boolean;
}

interface BrowserAddressCommandsOptions {
  readonly activeTab: BrowserTabSnapshot | null;
  readonly blockingBrowserError: string | null;
  readonly browserActionBusy: boolean;
  readonly browserAddressBusy: boolean;
  readonly devTunnelRecordsRef: MutableRefObject<ReadonlyArray<DevTunnelRecord>>;
  readonly ensureSession: (
    initialUrl: string,
    options?: EnsureBrowserSessionOptions,
  ) => Promise<boolean>;
  readonly environmentId: EnvironmentId;
  readonly liveDisplayIsAttached: boolean;
  readonly project: { readonly name: string } | null | undefined;
  readonly previewRememberLastUrl: boolean;
  readonly readBrowserAddressValue: (override?: string | null) => string;
  readonly restartBrowserSessionAtUrl: (initialUrl: string) => Promise<boolean>;
  readonly runAddressCommand: (command: BrowserAddressCommand) => Promise<boolean>;
  readonly session: BrowserSessionSnapshot | null;
  readonly setAddressValue: (value: string) => void;
  readonly setThreadDevPreviewUrl: (value: string | null) => void;
  readonly setTrackedDevTunnelRecords: Dispatch<SetStateAction<ReadonlyArray<DevTunnelRecord>>>;
  readonly threadId: ThreadId;
  readonly updateSettings: (patch: { readonly previewLastUrl: string }) => void;
}

interface BrowserAddressCommands {
  readonly ensureDevTunnelPreviewUrl: (input: {
    readonly allocatedPort: number;
    readonly label?: string;
  }) => Promise<string>;
  readonly submitBrowserAddress: (
    mode: "navigate-active-tab" | "new-tab",
    rawAddressValueOverride?: string | null,
    options?: { readonly retryHiddenLocalPreviewFailure?: boolean },
  ) => Promise<boolean>;
}

export function useBrowserAddressCommands({
  activeTab,
  blockingBrowserError,
  browserActionBusy,
  browserAddressBusy,
  devTunnelRecordsRef,
  ensureSession,
  environmentId,
  liveDisplayIsAttached,
  project,
  previewRememberLastUrl,
  readBrowserAddressValue,
  restartBrowserSessionAtUrl,
  runAddressCommand,
  session,
  setAddressValue,
  setThreadDevPreviewUrl,
  setTrackedDevTunnelRecords,
  threadId,
  updateSettings,
}: BrowserAddressCommandsOptions): BrowserAddressCommands {
  const ensureDevTunnelPreviewUrl = useCallback(
    async (input: { readonly allocatedPort: number; readonly label?: string }): Promise<string> => {
      const api = ensureEnvironmentApi(environmentId);
      if (!api.devTunnel) {
        return `http://127.0.0.1:${input.allocatedPort}/`;
      }

      const listedTunnels = await api.devTunnel.list({ threadId });
      setTrackedDevTunnelRecords(listedTunnels);
      const existingTunnel = listedTunnels.find(
        (tunnel) => tunnel.localPort === input.allocatedPort && hasReusableDevTunnelAccess(tunnel),
      );
      const tunnel =
        existingTunnel ??
        (await api.devTunnel.register({
          threadId,
          localHost: "127.0.0.1",
          localPort: input.allocatedPort,
          label: input.label ?? "Forge browser preview",
        }));
      setTrackedDevTunnelRecords((records) => upsertDevTunnelRecord(records, tunnel));
      setThreadDevPreviewUrl(tunnel.publicUrl);
      return tunnel.publicUrl;
    },
    [environmentId, setThreadDevPreviewUrl, setTrackedDevTunnelRecords, threadId],
  );

  const resolveBrowserAddressCommand = useCallback(
    async (
      mode: "navigate-active-tab" | "new-tab",
      rawAddressValue: string,
    ): Promise<BrowserAddressCommand | null> => {
      const command = buildBrowserAddressCommand({
        addressValue: rawAddressValue,
        activeTabId: activeTab?.tabId ?? null,
        mode,
      });
      if (!command) {
        return null;
      }

      const localPort = localDevServerPreviewPort(command.url);
      if (localPort) {
        const previewUrl = await ensureDevTunnelPreviewUrl({
          allocatedPort: localPort,
          label: project?.name ? `${project.name} browser preview` : "Forge browser preview",
        });
        setAddressValue(command.url);
        return { ...command, url: previewUrl };
      }

      return command;
    },
    [activeTab?.tabId, ensureDevTunnelPreviewUrl, project?.name, setAddressValue],
  );

  const submitBrowserAddress = useCallback(
    async (
      mode: "navigate-active-tab" | "new-tab",
      rawAddressValueOverride?: string | null,
      options: { readonly retryHiddenLocalPreviewFailure?: boolean } = {},
    ): Promise<boolean> => {
      const rawAddressValue = readBrowserAddressValue(rawAddressValueOverride);
      const command = await resolveBrowserAddressCommand(mode, rawAddressValue);
      if (!command) {
        toastManager.add({
          type: "warning",
          title: "Invalid browser URL",
          description: buildInvalidBrowserAddressMessage(rawAddressValue),
        });
        return false;
      }
      const commitSuccessfulAddress = () => {
        const rememberedAddress = browserRememberedAddress(
          {
            rawAddressValue,
            commandUrl: command.url,
          },
          devTunnelRecordsRef.current,
        );
        if (previewRememberLastUrl) {
          updateSettings({
            previewLastUrl: rememberedAddress,
          });
        }
        setAddressValue(rememberedAddress);
      };
      const submitAction = resolveBrowserAddressSubmitAction({
        browserAddressBusy,
        hasSession: Boolean(session),
        browserActionBusy,
        activeTab,
        hasBlockingError: Boolean(blockingBrowserError),
        liveDisplayIsAttached,
        commandUrl: command.url,
        session,
        devTunnelRecords: devTunnelRecordsRef.current,
      });
      if (submitAction === "blocked") {
        return false;
      }
      if (submitAction === "open-session") {
        const ok = await ensureSession(command.url, {
          allowWhileConnecting: true,
          ...(options.retryHiddenLocalPreviewFailure === undefined
            ? {}
            : { retryHiddenLocalPreviewFailure: options.retryHiddenLocalPreviewFailure }),
        });
        if (ok) {
          commitSuccessfulAddress();
        }
        return ok;
      }
      if (submitAction === "restart-session") {
        const ok = await restartBrowserSessionAtUrl(command.url);
        if (ok) {
          commitSuccessfulAddress();
        }
        return ok;
      }
      const ok = await runAddressCommand(command);
      if (ok) {
        commitSuccessfulAddress();
      }
      return ok;
    },
    [
      activeTab,
      blockingBrowserError,
      browserActionBusy,
      browserAddressBusy,
      devTunnelRecordsRef,
      ensureSession,
      liveDisplayIsAttached,
      previewRememberLastUrl,
      readBrowserAddressValue,
      resolveBrowserAddressCommand,
      restartBrowserSessionAtUrl,
      runAddressCommand,
      session,
      setAddressValue,
      updateSettings,
    ],
  );

  return {
    ensureDevTunnelPreviewUrl,
    submitBrowserAddress,
  };
}
